import { useCallback, useEffect, useRef, useState } from "react";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";

export const useAzureSpeechToText = ({
  speechKey,
  speechRegion,
  supportedLanguages = ["en-US"],
  onFinalResult,
  onError,
}) => {
  const recognizerRef = useRef(null);
  const [isListening, setIsListening] = useState(false);

  const stopListening = useCallback(() => {
    try {
      if (recognizerRef.current) {
        recognizerRef.current.stopContinuousRecognitionAsync(() => {
          recognizerRef.current.close();
          recognizerRef.current = null;
        });
      }
    } catch (_) {
      // no-op
    } finally {
      setIsListening(false);
    }
  }, []);

  const startListening = useCallback(() => {
    if (!speechKey || !speechRegion) {
      onError?.("Speech-to-text is not configured (missing key/region).");
      return;
    }

    try {
      setIsListening(true);

      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
        speechKey,
        speechRegion
      );

      // most reliable single-language setup
      speechConfig.speechRecognitionLanguage = supportedLanguages?.[0] || "en-US";

      speechConfig.setProperty(
        SpeechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs,
        "50000"
      );

      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();

      // fallback-friendly recognizer creation
      let recognizer = null;
      const canAutoDetect =
        SpeechSDK.AutoDetectSourceLanguageConfig?.fromLanguages &&
        SpeechSDK.SpeechRecognizer?.FromConfig;

      if (canAutoDetect && supportedLanguages?.length > 1) {
        const autoDetectLangConfig =
          SpeechSDK.AutoDetectSourceLanguageConfig.fromLanguages(
            supportedLanguages
          );

        recognizer = SpeechSDK.SpeechRecognizer.FromConfig(
          speechConfig,
          autoDetectLangConfig,
          audioConfig
        );
      } else {
        recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
      }

      recognizerRef.current = recognizer;

      recognizer.recognizing = (_, e) => {
        // interim
        // console.debug("🎤 Recognizing:", e.result.text);
      };

      recognizer.recognized = (_, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          onFinalResult?.(e.result.text);
        }
      };

      recognizer.canceled = () => {
        stopListening();
        onError?.("Speech recognition canceled. Check mic permission.");
      };

      recognizer.sessionStopped = () => {
        stopListening();
      };

      recognizer.startContinuousRecognitionAsync(
        () => {
          // started
        },
        (err) => {
          console.error("[STT] start error", err);
          onError?.("Could not start microphone. Check permissions.");
          stopListening();
        }
      );
    } catch (err) {
      console.error("[STT] init error", err);
      onError?.("Could not start speech-to-text. Check mic permission/config.");
      setIsListening(false);
    }
  }, [
    speechKey,
    speechRegion,
    supportedLanguages,
    onFinalResult,
    onError,
    stopListening,
  ]);

  const toggleListening = useCallback(() => {
    if (isListening) stopListening();
    else startListening();
  }, [isListening, startListening, stopListening]);

  useEffect(() => {
    return () => stopListening();
  }, [stopListening]);

  return { isListening, startListening, stopListening, toggleListening };
};
